import React, {useCallback, useMemo} from 'react';
import {Link, useLocation, useParams, useSearchParams} from 'react-router-dom';
import {useProductContext} from 'contexts/ProductContext';
import styled from 'styled-components';

interface BreadcrumbsProps {
    isHardware?: boolean;
    hideLast?: boolean;
}

export const Breadcrumbs = ({
    isHardware = false,
    hideLast = false,
}: BreadcrumbsProps) => {
    const {jobId} = useParams();
    const {breadcrumbs, popBreadCrumb} = useProductContext();
    const location = useLocation();
    const [searchParams] = useSearchParams();

    const popBreadcrumbHandler = useCallback(
        (index: number) => () => {
            if (popBreadCrumb) {
                popBreadCrumb(index);
            }
        },
        [popBreadCrumb]
    );

    const isPlaceholderCatalog = searchParams.get('category') === 'placeholder';

    const backButton = useMemo(() => {
        const hasSundry = searchParams.has('sundry');
        const hasProduct = searchParams.has('product');

        if (
            breadcrumbs &&
            breadcrumbs.length > 1 &&
            !hasSundry &&
            !hasProduct
        ) {
            const breadcrumb = breadcrumbs[breadcrumbs.length - 2];

            if (breadcrumb) {
                return (
                    <BackButton
                        onClick={popBreadcrumbHandler(breadcrumbs.length - 1)}
                        to={breadcrumb.link ?? ''}>
                        {isPlaceholderCatalog ? (
                            <>&lt; All {breadcrumb.key}</>
                        ) : (
                            <>&lt; All {breadcrumb.key} products</>
                        )}
                    </BackButton>
                );
            }
        }
    }, [breadcrumbs, searchParams, isPlaceholderCatalog]);

    return (
        <ul>
            {backButton ? <li>{backButton}</li> : null}
            <li>
                <Link
                    onClick={popBreadcrumbHandler(-1)}
                    to={
                        isHardware
                            ? `/v2/job/${jobId}/hardware`
                            : isPlaceholderCatalog
                              ? `${location.pathname}?category=placeholder`
                              : location.pathname
                    }>
                    {isPlaceholderCatalog
                        ? 'All Placeholders'
                        : 'All Categories'}
                </Link>
            </li>
            {breadcrumbs
                ? breadcrumbs.map((item, index) => {
                      if (item.link)
                          return (
                              <li key={item.key}>
                                  <Link
                                      onClick={popBreadcrumbHandler(index)}
                                      to={item.link}>
                                      {item.key}
                                  </Link>
                              </li>
                          );
                      else
                          return (
                              <li
                                  key={item.key}
                                  style={
                                      hideLast
                                          ? {
                                                display:
                                                    index ==
                                                    breadcrumbs.length - 1
                                                        ? 'none'
                                                        : 'block',
                                            }
                                          : {}
                                  }>
                                  <a>{item.key}</a>
                              </li>
                          );
                  })
                : null}
        </ul>
    );
};

const BackButton = styled(Link)`
    background: ${({theme}) => theme.colors.primary?.main};
    color: white !important;
    text-decoration: none;
    padding: 2px 5px;
    border-radius: 5px;
    border: 0;
`;
